import * as Logger from "./logger.js";
import * as Utils from './utils.js'
import { HSComponent } from "./component.js";
import { UserSession } from './session.js';
import styles from "./selectbox.css";
import template from "./selectbox.html";
import itemTemplate from "./selectbox-item.html";

export class HSSelectBox extends HSComponent {
    searchText = '';
    items = [];
    action = null;

    constructor() {
        super();
    }

    onLoad() {
        this.loadTemplate(template);
        this.loadStyles(styles);
    }

    init({ src, action, placeholder = '', multiSelect = false }) {
        this.src = src;
        this.action = action;
        this.multiSelect = multiSelect;
        this.placeholder = placeholder;

        // we store a 'this'-bound handler so we can remove it later
        this.outsideClickBoundHandler = this.outsideClickListener.bind(this);

        if (!this.multiSelect) {
            const selectedLabel = this.shadowRoot.querySelector('.selected-label');
            if (selectedLabel) { selectedLabel.remove(); }
        }

        this.searchInput = this.shadowRoot.querySelector('#search-input');
        this.itemsContainer = this.shadowRoot.querySelector('#dropdown-items');
        this.selectbox = this.shadowRoot.querySelector('.dropdown-textbox-content');
        this.dropdown = this.shadowRoot.querySelector('.dropdown-main-container');
        this.displayedValue = this.shadowRoot.querySelector('.dropdown-textbox-title');

        // prevent closing when clicking inside dropdown by stopping event propagation
        this.onEvent('click', (obj, ev) => ev.stopPropagation(), this.dropdown);

        // Search input event
        this.onEvent('input', (opts, e) => {
            Logger.debug("input: ", { opts, e });
            //this.filterOrganizations(e.target.value); 
            this.searchText = e.target.value.toLowerCase().trim();
            this.renderItems();
        }, this.searchInput);

        // Keyboard navigation (optional enhancement)
        this.onEvent('keydown', (opts, e) => {
            Logger.debug("keydown: ", { opts, e });
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                this.focusFirstItem();
            }
        }, this.searchInput);

        this.onEvent('click', (opts, e) => {
            this.toggleDropdown();
        }, this.selectbox);

        this.reloadData();
    }

    reloadItems() {
        if (this.src) {
            if (this.src.list) {
                if (this.src.list.type == 'session') {
                    this.items = UserSession.get(this.src.list.key)
                        .filter(item => item != null && typeof item === 'object' && item.id && item.name);
                }
            }
        }
    }

    getSelectedItems() {
        if (this.src) {
            if (this.src.selection) {
                if (this.src.selection.type == 'session') {
                    const selected_items = UserSession.get(this.src.selection.key);
                    if (Array.isArray(selected_items)) {
                        if (selected_items.length != this.items.length) {
                            return selected_items;
                        }
                        // fall through to return empty array if lengths match (all items selected)
                    }
                }
            }
        }
        return [];
    }

    reloadData() {
        if (this.src) {
            this.reloadItems();
            Logger.info("SelectBox items: ", this.items);
            const selected_items = this.getSelectedItems();

            if (selected_items.length == 0 && this.items.length == 1) {
                // If there is only one item in the list, select it by default
                Logger.info("SelectBox only one item in list, selecting it by default: ", this.items[0]);
                selected_items.push(this.items[0]);
            }

            if (selected_items.length > 1) {
                Logger.error("SelectBox multi-select support not implemented yet: ", selected_items);
            } else if (selected_items.length == 1) {
                const selected_item = selected_items[0];
                Logger.info("SelectBox session selected item: ", selected_item);
                const found_item = this.items.find(item => item.id === selected_item.id);
                if (found_item) {
                    Logger.info("selected item found from items list by id: ", found_item);
                    const current_selection = this.items.find(item => item.selected);
                    Logger.info("current selection displayed: ", current_selection);
                    if (!current_selection || current_selection.id != found_item.id) {
                        Logger.info("updating current selection: ", found_item);
                        this.toggleSelection(found_item.id, false);
                    }
                } else {
                    Logger.error("SelectBox selected item not found in list: ", selected_item);
                }
            } else {
                Logger.info("SelectBox clearing selections");
                this.clearSelections();
            }
            if (this.dropdown.classList.contains('open')) {
                this.toggleDropdown();
            }
            this.setTitle();
        }
    }

    setTitle() {
        const title = this.items.find(item => item.selected)?.name;
        if (title) {
            this.displayedValue.textContent = title;
            this.displayedValue.classList.remove('placeholder');
        } else {
            this.displayedValue.textContent = this.placeholder;
            this.displayedValue.classList.add('placeholder');
        }
    }

    toggleDropdown() {
        this.dropdown.classList.toggle('open');
        if (this.dropdown.classList.contains('open')) {
            // scroll to the top
            const dropdownContent = this.shadowRoot.querySelector('.dropdown-content');
            dropdownContent.scrollTop = 0;
            // register outside click listener
            setTimeout(() => {
                document.addEventListener('click', this.outsideClickBoundHandler);
            }, 0); // Use setTimeout to ensure the dropdown is fully rendered before adding the listener
            this.renderItems();
        } else {
            document.removeEventListener('click', this.outsideClickBoundHandler);
        }
    }

    outsideClickListener(ev) {
        // NOTE: we stop propagation on clicks inside the dropdown, so any click event reaching here is outside
        setTimeout(() => {
            this.toggleDropdown();
        }, 50);
    }

    createItem(item) {
        const div = document.createElement('div');
        div.className = `dropdown-item ${item.selected ? 'selected' : ''}`;
        div.setAttribute('tabindex', '0'); // Make focusable

        const el = document.createElement('template');
        Utils.setSafeHtml(el, Utils.evaluate(itemTemplate, item));
        const clone = el.content.cloneNode(true);
        div.appendChild(clone);

        this.onEvent("click", () => this.toggleSelection(item.id), div);
        this.onEvent("keydown", (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.toggleSelection(item.id);
            }
        }, div);

        return div;
    }

    toggleSelection(id, render = true) {
        const index = this.items.findIndex(item => item.id === id);
        if (index !== -1) {
            this.items[index].selected = !this.items[index].selected;
            if (!this.multiSelect) {
                // deselect all other items
                this.items.forEach((item, i) => { if (i !== index) { item.selected = false; } });
                if (render) { this.toggleDropdown(); }
            }
            if (render) {
                this.setTitle();
                this.renderItems();
                this.emit({ type: this.action, selected: this.items.filter(item => item.selected) });
            }
        }
    }

    clearSelections() {
        // deselect all items
        this.items.forEach(item => item.selected = false);
    }

    renderItems() {
        this.itemsContainer.innerHTML = '';

        const filteredItems = this.items.filter(item => this.searchText == '' ? true : item.name.toLowerCase().includes(this.searchText));
        if (filteredItems.length === 0) {
            this.itemsContainer.innerHTML = '<div class="dropdown-empty">No items found</div>';
            return;
        }

        // Sort: selected first
        const sortedItems = [...filteredItems].sort((a, b) => {
            if (a.selected && !b.selected) return -1;
            if (!a.selected && b.selected) return 1;
            // If both are selected or both are not selected, sort by name
            if (!a.name) { Logger.debug("Item without name found during sorting: ", a); return 1; }
            if (!b.name) { Logger.debug("Item without name found during sorting: ", b); return -1; }
            return a.name.localeCompare(b.name);
        });

        sortedItems.forEach(item => {
            const div = this.createItem(item);
            this.itemsContainer.appendChild(div);
        });
    }

    focusFirstItem() {
        const firstItem = this.itemsContainer.querySelector('.dropdown-item');
        if (firstItem) {
            firstItem.focus();
        }
    }

    // Public method to update data
    updateItems(items) {
        this.items = [...items].filter(item => item != null && typeof item === 'object' && item.id && item.name);
        this.renderItems();
    }

    // Public method to clear search
    clearSearch() {
        this.searchInput.value = '';
        this.searchText = '';
        this.renderItems();
    }
}
customElements.define("hs-selectbox", HSSelectBox);
